import React from 'react';
import { Form } from 'react-bootstrap';

// CSS styles as objects
const toggleContainerStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  margin: '6px 0',
  padding: '6px 0',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  borderRadius: '6px',
};

const toggleContainerHoverStyle = {
  ...toggleContainerStyle,
  backgroundColor: 'rgba(0, 0, 0, 0.04)',
};

const labelStyle = {
  fontSize: '0.9rem',
  fontWeight: 500,
  color: '#495057',
  flexGrow: 1,
  marginRight: '12px',
  transition: 'color 0.15s ease-in-out',
};

const toggleStyle = {
  '.form-check-input': {
    height: '1.25rem',
    width: '2.25rem',
    cursor: 'pointer',
    position: 'relative',
    marginTop: 0,
    boxShadow: 'none',
  }
};

// Custom CSS for toggle switch (to be added to the component)
const customToggleCSS = `
  .custom-toggle .form-check-input:checked {
    background-color: #ff7f00;
    border-color: #ff7f00;
  }
  
  .custom-toggle .form-check-input:checked:focus {
    box-shadow: 0 0 0 0.2rem rgba(255, 127, 0, 0.25);
  }
  
  .custom-toggle .form-check-input:focus {
    box-shadow: 0 0 0 0.2rem rgba(49, 55, 74, 0.25);
  }
  
  .custom-toggle .form-check-label {
    margin-left: 0.5rem;
  }
`;

interface ColumnVisibilityToggleProps {
  id: string;
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

const ColumnVisibilityToggle: React.FC<ColumnVisibilityToggleProps> = ({ 
  id, 
  label, 
  checked, 
  onChange 
}) => {
  const [isHovered, setIsHovered] = React.useState(false);
  
  // Add custom CSS to document head
  React.useEffect(() => {
    // Add CSS if not already added
    if (!document.getElementById('column-toggle-styles')) {
      const styleElement = document.createElement('style');
      styleElement.id = 'column-toggle-styles';
      styleElement.innerHTML = customToggleCSS;
      document.head.appendChild(styleElement);
      
      // Clean up on unmount
      return () => {
        const existingStyle = document.getElementById('column-toggle-styles');
        if (existingStyle && existingStyle.parentNode === document.head) {
          document.head.removeChild(existingStyle);
        }
      };
    }
  }, []);
  
  return (
    <div 
      style={isHovered ? toggleContainerHoverStyle : toggleContainerStyle}
      role="button" 
      onClick={() => onChange(!checked)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onChange(!checked);
        }
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      tabIndex={0}
      aria-checked={checked}
    >
      <span style={labelStyle}>{label}</span>
      <Form.Check
        className="custom-toggle"
        type="switch"
        id={`column-switch-${id}`}
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        aria-label={`Toggle visibility of ${label} column`}
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
};

export default ColumnVisibilityToggle;
